// Planner — Activiteit detail (the legacy "selectable activity" drill-in). Opened from the Regime
// configureren list: an Activity's meta + per-tenant overlay (Inschakelen / verantwoordelijke) + its
// linked Bewijsmiddelen ("bewijslast" / means of proof) with the same in-orde/ontbrekend evidence
// status the Controller Verantwoording computes. Backend: GET /regime/activities/:id +
// POST /regime (toggle). RLS-scoped; gated verwerking.read / verwerking.write.
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ApiError } from "@sympllex/api-client";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { useSession } from "../../lib/session";
import { hasPermission } from "@sympllex/auth";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronLeft, CheckCircle2, AlertCircle, ListChecks, ShieldCheck } from "lucide-react";

type ActivityType = "verwerking" | "organisatie" | "beide";
interface MeansOfProof { containerId: string; containerName: string | null; satisfied: boolean; fileCount: number }
interface ChecklistItem { id: string; text: string }
interface Checklist {
  id: string; code: string | null; title: string; kind: string; articleRef: string | null;
  items: ChecklistItem[]; children: Checklist[];
}
interface ProofRequirement { id: string; category: string; label: string }
interface ActivityDetail {
  id: string; name: string; description: string | null;
  shortDescription: string | null; mandatoryCitation: string | null;
  activityType: ActivityType; activityCode: string | null; sectionCode: string | null;
  hoofddoelId: string | null; hoofddoelName: string | null;
  enabled: boolean; isDefault: boolean; assignedUserId: string | null;
  meansOfProof: MeansOfProof[]; inOrde: boolean;
  checklists: Checklist[]; proofRequirements: ProofRequirement[];
}

const errText = (e: unknown) => (e instanceof ApiError ? `${e.status}` : (e as Error).message);
const TYPE_VARIANT: Record<ActivityType, "default" | "secondary" | "outline"> = {
  verwerking: "default", organisatie: "secondary", beide: "outline",
};

/** Renders one checklist (applicability | compliance) with its questions and any nested sub-checklists. */
function ChecklistNode({ node, t, depth = 0 }: { node: Checklist; t: (k: string) => string; depth?: number }) {
  const kindLabel = node.kind === "applicability" ? t("activity.detail.kind.applicability") : t("activity.detail.kind.compliance");
  return (
    <div className={depth > 0 ? "border-l pl-3" : ""}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{[node.code, node.title].filter(Boolean).join(" · ")}</span>
        <Badge variant={node.kind === "applicability" ? "secondary" : "outline"}>{kindLabel}</Badge>
        {node.articleRef && <Badge variant="ghost">{node.articleRef}</Badge>}
      </div>
      {node.items.length > 0 && (
        <ul className="mt-1.5 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          {node.items.map((it) => <li key={it.id}>{it.text}</li>)}
        </ul>
      )}
      {node.children.length > 0 && (
        <div className="mt-2 space-y-3">
          {node.children.map((c) => <ChecklistNode key={c.id} node={c} t={t} depth={depth + 1} />)}
        </div>
      )}
    </div>
  );
}

export function ActivityDetailRoute() {
  const { t } = useI18n();
  const { activityId } = useParams({ from: "/activities/regime/$activityId" });
  const session = useSession();
  const canWrite = hasPermission(session, "verwerking.write");
  const qc = useQueryClient();

  const q = useQuery({ queryKey: ["activity-detail", activityId], queryFn: () => api.getActivityDetail(activityId) as Promise<ActivityDetail> });
  const a = q.data;

  const toggle = useMutation({
    mutationFn: (enabled: boolean) => api.setActivityEnabled(activityId, enabled),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["activity-detail", activityId] });
      qc.invalidateQueries({ queryKey: ["regime", "overlay"] });
    },
  });

  return (
    <section>
      <Link to="/activities/regime" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="size-4" /> {t("activity.detail.back")}
      </Link>

      {q.isLoading ? (
        <div className="space-y-3"><Skeleton className="h-9 w-2/3" /><Skeleton className="h-24 w-full" /><Skeleton className="h-40 w-full" /></div>
      ) : q.error ? (
        <p className="text-sm text-destructive">{t("regime.failed")} {errText(q.error)}</p>
      ) : !a ? (
        <p className="text-sm text-muted-foreground">{t("activity.detail.notFound")}</p>
      ) : (
        <>
          <PageHeader
            title={[a.activityCode, a.name].filter(Boolean).join(" · ")}
            {...(a.sectionCode ? { description: `${t("regime.section")} ${a.sectionCode}` } : {})}
          />

          <div className="mb-4 flex flex-wrap items-center gap-2">
            <Badge variant={TYPE_VARIANT[a.activityType]}>{t(`regime.type.${a.activityType}`)}</Badge>
            {a.hoofddoelName && <Badge variant="ghost">{t("activity.detail.hoofddoel")}: {a.hoofddoelName}</Badge>}
            {a.inOrde
              ? <Badge variant="outline" className="text-emerald-700"><CheckCircle2 className="mr-1 size-3.5" />{t("activity.detail.inOrde")}</Badge>
              : <Badge variant="outline" className="text-amber-700"><AlertCircle className="mr-1 size-3.5" />{t("activity.detail.ontbrekend")}</Badge>}
          </div>

          <div className="grid gap-4">
            {/* Inschakelen + toelichting */}
            <Card>
              <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
                <CardTitle className="text-base">{t("activity.detail.enabled")}</CardTitle>
                <Switch
                  checked={a.enabled}
                  disabled={!canWrite || toggle.isPending}
                  onCheckedChange={(v) => toggle.mutate(v)}
                  aria-label={`${t("regime.col.enable")} ${a.activityCode ?? a.name}`}
                />
              </CardHeader>
              <CardContent>
                {toggle.isError && <p className="mb-2 text-sm text-destructive">{t("regime.saveFailed")} {errText(toggle.error)}</p>}
                {a.mandatoryCitation?.trim() && (
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {t("activity.detail.citation")}: <span className="font-normal normal-case">{a.mandatoryCitation}</span>
                  </p>
                )}
                {a.shortDescription?.trim() && <p className="mb-2 text-sm font-medium">{a.shortDescription}</p>}
                <h3 className="mb-1 text-sm font-medium">{t("activity.detail.description")}</h3>
                <p className="whitespace-pre-line text-sm text-muted-foreground">{a.description?.trim() || t("activity.detail.noDescription")}</p>
              </CardContent>
            </Card>

            {/* Checklists — applicability + per-article compliance (the legacy Toelichting §x.x.1) */}
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2 text-base"><ListChecks className="size-4" />{t("activity.detail.checklists")}</CardTitle></CardHeader>
              <CardContent>
                {a.checklists.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t("activity.detail.checklistsEmpty")}</p>
                ) : (
                  <div className="space-y-4">
                    {a.checklists.map((c) => <ChecklistNode key={c.id} node={c} t={t} />)}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Bewijsvoering — initieel + procedureel proof requirements (the legacy Toelichting §x.x.2) */}
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2 text-base"><ShieldCheck className="size-4" />{t("activity.detail.bewijsvoering")}</CardTitle></CardHeader>
              <CardContent>
                {a.proofRequirements.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t("activity.detail.proofEmpty")}</p>
                ) : (
                  <div className="space-y-3">
                    {(["initial", "procedure"] as const).map((cat) => {
                      const reqs = a.proofRequirements.filter((p) => p.category === cat);
                      if (reqs.length === 0) return null;
                      return (
                        <div key={cat}>
                          <h3 className="mb-1 text-sm font-medium">{t(`activity.detail.proof.${cat}`)}</h3>
                          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                            {reqs.map((p) => <li key={p.id}>{p.label}</li>)}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Bewijsmiddelen (means of proof) */}
            <Card>
              <CardHeader><CardTitle className="text-base">{t("activity.detail.meansOfProof")}</CardTitle></CardHeader>
              <CardContent>
                {a.meansOfProof.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t("activity.detail.meansEmpty")}</p>
                ) : (
                  <ul className="m-0 list-none p-0">
                    {a.meansOfProof.map((m) => (
                      <li key={m.containerId} className="flex items-center justify-between gap-3 border-b py-2.5 text-sm last:border-b-0">
                        <span className="min-w-0 truncate font-medium">{m.containerName ?? "—"}</span>
                        <span className="flex shrink-0 items-center gap-3">
                          <span className="text-xs text-muted-foreground">{m.fileCount} {t("activity.detail.files")}</span>
                          {m.satisfied
                            ? <Badge variant="outline" className="text-emerald-700"><CheckCircle2 className="mr-1 size-3.5" />{t("activity.detail.inOrde")}</Badge>
                            : <Badge variant="outline" className="text-amber-700"><AlertCircle className="mr-1 size-3.5" />{t("activity.detail.ontbrekend")}</Badge>}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </section>
  );
}
